import { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getTokenPair } from '../api/auth';
import { jwtDecode } from 'jwt-decode';
import { AdminUser, setAdminUserInfo } from '../store/adminUserSlice';
import { parseHash, setTokens, getAccessToken, clearTokens } from '../utils';
import { useDispatch } from 'react-redux';
import Loader from '../components/BaseComponents/Loader';

interface IAuth {
  isAuthenticated: boolean;
}

// Helper function to validate a JWT token
const isValidToken = (token: string): boolean => {
  if (!token || token === 'undefined') return false;
  const parts = token.split('.');
  return parts.length === 3;
};

export const AuthContext = createContext<any>({});

export const AuthProvider = ({ children }: any) => {
  const [auth, setAuth] = useState<IAuth>({ isAuthenticated: false });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleAuth = useCallback(async () => {
    const hash = location.hash;
    if (hash) {
      const params = parseHash(hash);
      if (params.access_token) {
        try {
          const tokens = await getTokenPair(params.access_token);
          if (tokens.access_token && tokens.refresh_token) {
            setTokens(tokens);
            const decodedToken: AdminUser = jwtDecode(tokens.access_token);
            const adminInfo = {
              role: decodedToken.role,
              user_id: decodedToken.user_id,
              is_super_admin: decodedToken.is_super_admin,
              email: decodedToken.email,
              first_name: decodedToken.first_name,
              last_name: decodedToken.last_name,
            };
            if (decodedToken.role === 'client') {
              clearTokens();
              navigate('/admin/87431/login', { replace: true });
              return;
            } else {
              dispatch(setAdminUserInfo(adminInfo));
              setAuth({ isAuthenticated: true });
              navigate('/', { replace: true });
              
            }
         
          } else {
            throw new Error('Invalid tokens received');
          }
        } catch (error) {
          clearTokens();
          navigate('/admin/87431/login', { replace: true });
        } finally {
          setIsLoading(false);
        }
      }
    } else {
      const existingAccessToken = getAccessToken();
      if (existingAccessToken && isValidToken(existingAccessToken)) {
        try {
          const decodedToken: AdminUser = jwtDecode(existingAccessToken);
          const adminInfo = {
            role: decodedToken.role,
            user_id: decodedToken.user_id,
            is_super_admin: decodedToken.is_super_admin,
            email: decodedToken.email,
            first_name: decodedToken.first_name,
            last_name: decodedToken.last_name,
          };
          dispatch(setAdminUserInfo(adminInfo));
          setAuth({ isAuthenticated: true });
        } catch (error) {
          clearTokens();
          setAuth({ isAuthenticated: false });
          navigate('/admin/87431/login', { replace: true });
        } finally {
          setIsLoading(false);
        }
      } else {
        clearTokens();
        setAuth({ isAuthenticated: false });
        setIsLoading(false);
        if (
          location.pathname !== '/admin/87431/login' &&
          location.pathname !== '/complete-profile' &&
          location.pathname !== '/delete-account' &&
          location.pathname !== '/book-test' &&
          location.pathname !== '/subscription' &&
          location.pathname !== '/confirmation' &&
          location.pathname !== '/complete-subscription' &&
          location.pathname !== '/complete-onboarding' &&
          location.pathname !== '/plans' &&
          location.pathname !== '/add-ons-plan' &&
          location.pathname !== '/blood-only-checkout' &&
          location.pathname !== '/plans-core' &&
          location.pathname !== '/pre-scan-questions' &&
          location.pathname !== '/pre-scan-questionnaire-pdf' &&
          location.pathname !== '/plans-plus' &&
          location.pathname !== '/upgrade-plan' &&
          location.pathname !== '/plans-scan' &&
          location.pathname !== '/full-body-scan' &&
          location.pathname !== '/personalised-plan' &&
          !location.pathname.startsWith('/personalised-plan/') &&
          location.pathname !== '/annual-scan' &&
          !location.pathname.startsWith('/annual-scan/') &&
          location.pathname !== '/lp-1' &&
          !location.pathname.startsWith('/lp-1/') &&
          !location.pathname.startsWith('/signup')
        ) {
          window.location.href = 'https://corevitals.me';
        }
      }
    }
  }, [location.pathname, location.hash, dispatch, navigate]);

  useEffect(() => {
    handleAuth();
  }, [handleAuth]);

  return (
    <AuthContext.Provider value={{ auth, setAuth, isLoading }}>
      {isLoading ? <Loader height='100vh' /> : children}
    </AuthContext.Provider>
  );
};
